import { Head, router } from '@inertiajs/react'
import { loadStripe } from '@stripe/stripe-js'
import { useState, useCallback } from 'react'
import ReservationLayout from '@/components/layouts/ReservationLayout'
import PharmacyMap from '@/components/common/PharmacyMap'
import MedicationMailNotice from '@/components/reservations/MedicationMailNotice'
import PrescriptionNotice from '@/components/reservations/PrescriptionNotice'
import type { ProvisionalReservation, PatientFamilyProfile } from '@/types/schedule'

type Pharmacy = {
  id: number
  name: string
  address: string
  tel: string
  lat: number | null
  lng: number | null
  is_alliance: boolean
}

type Props = {
  provisional_reservation: ProvisionalReservation
  patient_family_profile: PatientFamilyProfile
  pharmacies: Pharmacy[]
  is_medication_mail: boolean
  selected_pharmacy_id: number | null
  favorite_pharmacy_id: number | null
  medication_mail_available: boolean
  medication_mail_only: boolean
  stripe_publishable_key: string
  errors: Record<string, string[]>
  pharmacy_receivable_from: string | null
  medication_mail_delivery_estimate: string
}

const MEDICATION_MAIL_FEE = 900

export default function InterviewStep4({
  provisional_reservation,
  patient_family_profile,
  pharmacies: initialPharmacies,
  is_medication_mail: initialIsMedicationMail,
  selected_pharmacy_id: initialSelectedPharmacyId,
  favorite_pharmacy_id: favoritePharmacyId,
  medication_mail_available,
  medication_mail_only,
  stripe_publishable_key,
  errors,
  pharmacy_receivable_from,
  medication_mail_delivery_estimate,
}: Props) {
  const [isMedicationMail, setIsMedicationMail] = useState(initialIsMedicationMail)
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<number | null>(initialSelectedPharmacyId)
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>(initialPharmacies)
  const [searchAddress, setSearchAddress] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pharmacyError, setPharmacyError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // 薬局検索
  const handleSearchPharmacies = useCallback(async () => {
    if (!searchAddress.trim()) return

    setIsSearching(true)
    setSearchError('')

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

    try {
      const response = await fetch(
        '/online_clinic/reservations/selectable_pharmacies',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({
            provisional_reservation_token: provisional_reservation.token,
            address: searchAddress.trim(),
          }),
        }
      )

      if (!response.ok) {
        setSearchError('薬局の検索に失敗しました。しばらく経ってからお試しください。')
        return
      }

      const data = await response.json()
      if (data.success) {
        setPharmacies(data.pharmacies)
        setSelectedPharmacyId(null)
        if (data.center_position) {
          setMapCenter(data.center_position)
        }
      } else {
        setSearchError(data.error || '薬局の検索に失敗しました。')
      }
    } catch (err) {
      console.error('薬局検索に失敗しました:', err)
      setSearchError('薬局の検索に失敗しました。しばらく経ってからお試しください。')
    } finally {
      setIsSearching(false)
    }
  }, [provisional_reservation.token, searchAddress])

  // Enterキーで検索
  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearchPharmacies()
    }
  }, [handleSearchPharmacies])

  // 予約確定モーダルを開く
  const handleConfirmReservation = useCallback(() => {
    if (!isMedicationMail && !selectedPharmacyId) {
      setPharmacyError('薬局を選択してください')
      return
    }

    setPharmacyError(null)
    setShowConfirmModal(true)
  }, [isMedicationMail, selectedPharmacyId])

  // 予約確定処理を実行
  // 受取方法を保存 → 与信取得(PaymentIntent作成) → クライアントで与信確定(3Dセキュア) → 予約確定。
  // サーバー側でconfirmすると3Dセキュア必須カードが与信未成立のまま予約確定してしまうため、
  // クライアントで stripe.confirmCardPayment を実行して与信（キャンセル料の担保）を成立させる(#1045)。
  const executeConfirmReservation = useCallback(async () => {
    setShowConfirmModal(false)
    setIsSubmitting(true)
    setSubmitError(null)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const jsonHeaders = { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }

    try {
      // 1. 受取方法を保存
      const saveResponse = await fetch(
        `/online_clinic/reservations/update_interview_step4?provisional_reservation_token=${provisional_reservation.token}`,
        {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({
            is_medication_mail: isMedicationMail,
            pharmacy_id: isMedicationMail ? null : selectedPharmacyId,
          }),
        }
      )
      if (!saveResponse.ok) throw new Error('Failed to save step4')

      // 2. 与信用のPaymentIntentを作成
      const stripe = await loadStripe(stripe_publishable_key)
      if (!stripe) throw new Error('Failed to load Stripe')

      const piResponse = await fetch(
        `/online_clinic/reservations/payment_intent?provisional_reservation_token=${provisional_reservation.token}`,
        { method: 'POST', headers: jsonHeaders }
      )
      if (!piResponse.ok) throw new Error('Failed to create PaymentIntent')
      const piData = await piResponse.json()

      // 3. クライアントで与信確定（3Dセキュアが必要な場合はここでチャレンジが表示される）
      const { error, paymentIntent } = await stripe.confirmCardPayment(piData.client_secret)
      // 与信が成立（requires_capture）しなかった場合は予約確定へ進まない。最終的な検証はサーバー側でも行う。
      if (
        error ||
        !paymentIntent ||
        paymentIntent.id !== piData.payment_intent_id ||
        paymentIntent.status !== 'requires_capture'
      ) {
        setIsSubmitting(false)
        setSubmitError(error?.message || '決済の確認に失敗しました。カード情報をご確認のうえ、再度お試しください。')
        return
      }

      // 4. 与信確定済みのPaymentIntentで予約確定
      router.post(
        `/online_clinic/reservations/confirm_reservation?provisional_reservation_token=${provisional_reservation.token}`,
        { payment_intent_id: piData.payment_intent_id },
        {
          onFinish: () => {
            setIsSubmitting(false)
          },
        }
      )
    } catch {
      setIsSubmitting(false)
      setSubmitError('エラーが発生しました。しばらく経ってからお試しください。')
    }
  }, [provisional_reservation.token, isMedicationMail, selectedPharmacyId, stripe_publishable_key])

  const previousPageLink = `/online_clinic/reservations/interview_step3?provisional_reservation_token=${provisional_reservation.token}`

  const selectedPharmacy = pharmacies.find((p) => p.id === selectedPharmacyId)

  const canConfirm = isMedicationMail || selectedPharmacyId

  return (
    <>
      <Head title="予約確認 - オンライン診療予約" />

      <ReservationLayout
        currentStep="interview4"
        previousPageLink={previousPageLink}
        title="予約確認"
        expiresAt={provisional_reservation.expires_at}
      >
        <div className="space-y-6">
          {/* 仮予約情報 */}
          <div className="oc-info-card">
            <h3 className="mb-3 text-base font-semibold text-[#2a3039]">仮予約中の診療枠</h3>
            <div className="space-y-1 text-sm text-[#515862]">
              <p>
                <span className="text-[#6f7680]">患者: </span>
                {patient_family_profile.full_name}
              </p>
              <p>
                <span className="text-[#6f7680]">診療科目: </span>
                {provisional_reservation.medical_department.name}
              </p>
              <p>
                <span className="text-[#6f7680]">クリニック: </span>
                {provisional_reservation.clinic.name}
              </p>
              <p>
                <span className="text-[#6f7680]">日時: </span>
                {provisional_reservation.doctor_schedule.formatted_date}{' '}
                {provisional_reservation.doctor_schedule.formatted_time}
              </p>
              <p>
                <span className="text-[#6f7680]">担当医師: </span>
                {provisional_reservation.doctor_schedule.doctor.full_name}
              </p>
              <PrescriptionNotice
                nightSlot={provisional_reservation.doctor_schedule.night_slot}
                expectedPrescriptionIssuedDate={provisional_reservation.doctor_schedule.expected_prescription_issued_date}
                pharmacyReceivableFrom={isMedicationMail ? null : provisional_reservation.doctor_schedule.pharmacy_receivable_from}
                className="mt-1"
              />
            </div>
            <MedicationMailNotice className="mt-3" deliveryEstimate={medication_mail_delivery_estimate} />
          </div>

          {/* エラー表示 */}
          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          )}

          {Object.keys(errors).length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <ul className="text-sm text-red-600 list-disc list-inside">
                {Object.entries(errors).map(([key, messages]) =>
                  messages.map((msg, i) => <li key={`${key}-${i}`}>{msg}</li>)
                )}
              </ul>
            </div>
          )}

          {/* 処方箋受取方法 */}
          <div className="oc-card">
            <h2 className="oc-section-title">処方せんの受け取り方法</h2>

            {medication_mail_only ? (
              // 肥満症外来はおうち便で固定
              <div className="p-4 border border-cyan-500 bg-cyan-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800">おくすりおうち便</span>
                  <span className="text-sm text-cyan-600 font-medium">{MEDICATION_MAIL_FEE}円</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  お薬をご自宅にお届けします。お届け目安は{medication_mail_delivery_estimate}です（配送状況により前後する場合があります）。
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  ※肥満症外来はおくすりおうち便のみご利用いただけます
                </p>
              </div>
            ) : (
              <>
                <p className="mb-6 text-sm text-[#6f7680]">処方せんの受け取り方法を選択してください。</p>

                <div className="space-y-4">
                  {/* 薬局で受け取り */}
                  <label
                    className={`block p-4 border rounded-lg cursor-pointer transition ${
                      !isMedicationMail ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start">
                      <input
                        type="radio"
                        name="delivery_method"
                        checked={!isMedicationMail}
                        onChange={() => setIsMedicationMail(false)}
                        className="oc-radio mt-1"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-800">薬局で受け取り</span>
                          <span className="text-sm text-gray-500">無料</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          お近くの薬局で処方せんを受け取り、お薬を受け取ります。
                          {!pharmacy_receivable_from && 'お急ぎの場合におすすめです。'}
                        </p>
                        {pharmacy_receivable_from && (
                          <p className="text-sm font-bold text-orange-600 mt-1">
                            ※土曜午後・日曜・祝日は薬局への処方せんの送付をおこなっていないため、お薬のお受け取りは{pharmacy_receivable_from}以降になります。
                            {medication_mail_available && 'ご自宅にお薬をお届けする「おくすりおうち便」の利用もご検討ください。'}
                          </p>
                        )}
                      </div>
                    </div>
                  </label>

                  {/* おくすりおうち便 */}
                  <label
                    className={`block p-4 border rounded-lg transition ${
                      medication_mail_available ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                    } ${isMedicationMail ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="flex items-start">
                      <input
                        type="radio"
                        name="delivery_method"
                        checked={isMedicationMail}
                        onChange={() => medication_mail_available && setIsMedicationMail(true)}
                        disabled={!medication_mail_available}
                        className="oc-radio mt-1"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-800">おくすりおうち便</span>
                          <span className="text-sm text-cyan-600 font-medium">{MEDICATION_MAIL_FEE}円</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          お薬をご自宅にお届けします。お届け目安は{medication_mail_delivery_estimate}です（配送状況により前後する場合があります）。
                        </p>
                        {!medication_mail_available && (
                          <p className="text-sm text-red-500 mt-1">
                            ※コロナ外来で東京都以外にお住まいの方はご利用いただけません
                          </p>
                        )}
                      </div>
                    </div>
                  </label>
                </div>
              </>
            )}
          </div>

          {/* 薬局選択（おくすりおうち便でない場合） */}
          {!isMedicationMail && !medication_mail_only && (
            <div className="oc-card">
              <h2 className="oc-section-title">薬局を選択</h2>
              <p className="mb-4 text-xs text-[#7f8388]">
                ここで選んだ薬局が今回の受け取り先になります。
              </p>

              {/* 住所検索 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">住所で薬局を検索</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={searchAddress}
                    onChange={(e) => setSearchAddress(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="例: 東京都渋谷区"
                    className="oc-input min-w-0"
                  />
                  <button
                    type="button"
                    onClick={handleSearchPharmacies}
                    disabled={isSearching}
                    className="button-primary !w-auto !h-auto shrink-0 whitespace-nowrap px-5"
                  >
                    {isSearching ? '検索中...' : '検索'}
                  </button>
                </div>
              </div>

              {/* 検索エラー表示 */}
              {searchError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-sm text-red-600">{searchError}</p>
                </div>
              )}

              {/* Google Map */}
              {(pharmacies.length > 0 || mapCenter) && (
                <div className="mb-4">
                  <PharmacyMap
                    pharmacies={pharmacies}
                    centerLat={mapCenter?.lat}
                    centerLng={mapCenter?.lng}
                    selectedPharmacyId={selectedPharmacyId}
                    onSelectPharmacy={(id) => {
                      setSelectedPharmacyId(id)
                      setPharmacyError(null)
                      // 選択された薬局のリスト項目にスクロール
                      setTimeout(() => {
                        document.getElementById(`pharmacy-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                      }, 0)
                    }}
                  />
                </div>
              )}

              {/* 薬局リスト */}
              <div className="space-y-2">
                {pharmacies.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4 text-center">
                    お近くの薬局が見つかりません。住所を入力して検索してください。
                  </p>
                ) : (
                  pharmacies.map((pharmacy) => (
                    <label
                      key={pharmacy.id}
                      id={`pharmacy-${pharmacy.id}`}
                      className={`block p-4 border rounded-lg cursor-pointer transition ${
                        selectedPharmacyId === pharmacy.id
                          ? 'border-cyan-500 bg-cyan-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start">
                        <input
                          type="radio"
                          name="pharmacy"
                          checked={selectedPharmacyId === pharmacy.id}
                          onChange={() => { setSelectedPharmacyId(pharmacy.id); setPharmacyError(null) }}
                          className="oc-radio mt-1"
                        />
                        <div className="ml-3 flex-1">
                          <span className="font-medium text-gray-800">{pharmacy.name}</span>
                          {pharmacy.id === favoritePharmacyId && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-cyan-100 px-2 py-0.5 text-xs font-medium text-cyan-700 align-middle">
                              お気に入り薬局
                            </span>
                          )}
                          <p className="text-sm text-gray-500 mt-1">{pharmacy.address}</p>
                          <p className="text-sm text-gray-500">TEL: {pharmacy.tel}</p>
                          {pharmacy.id === favoritePharmacyId && (
                            <p className="mt-2 text-xs text-[#7f8388]">
                              お気に入り薬局が違う場合は、会員情報ページから変更してください。
                            </p>
                          )}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* 薬局選択バリデーションエラー */}
          {pharmacyError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-600">{pharmacyError}</p>
            </div>
          )}

          {/* 選択内容の確認 */}
          <div className="oc-card">
            <h2 className="oc-section-title">お受け取り方法</h2>
            {isMedicationMail ? (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-800">おくすりおうち便</p>
                  <p className="text-sm text-gray-500">お薬をご自宅にお届けします。お届け目安は{medication_mail_delivery_estimate}です（配送状況により前後する場合があります）。</p>
                </div>
                <span className="text-cyan-600 font-medium">{MEDICATION_MAIL_FEE}円</span>
              </div>
            ) : selectedPharmacy ? (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-800">{selectedPharmacy.name}</p>
                <p className="text-sm text-gray-500">{selectedPharmacy.address}</p>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">薬局を選択してください</p>
            )}
          </div>

          {/* 料金案内 */}
          <div className="oc-card">
            <h2 className="oc-section-title">料金について</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">オンライン診療手数料</span>
                <span className="font-medium">1,100円（税込）</span>
              </div>

              {isMedicationMail && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">おくすりおうち便</span>
                  <span className="font-medium">{MEDICATION_MAIL_FEE}円</span>
                </div>
              )}
              <div className="flex justify-between py-2">
                <span className="text-gray-600">保険診療費</span>
                <span className="text-gray-500">診察後に確定</span>
              </div>
            </div>
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
              予約時に2,000円の仮決済を行います。実際の請求は診察完了後に行われます。
            </div>
          </div>
        </div>

        {/* ナビゲーションボタン */}
        <div className="oc-action-bar">
          <div className="oc-action-container">
            <button
              type="button"
              onClick={() => router.visit(previousPageLink)}
              className="button-secondary"
            >
              前へ
            </button>
            <button
              type="button"
              onClick={handleConfirmReservation}
              disabled={isSubmitting || !canConfirm}
              className="button-primary"
            >
              {isSubmitting ? '予約中...' : '予約を確定する'}
            </button>
          </div>
        </div>

        {/* ボタン分のスペース確保 */}
        <div className="h-24" />

        {/* 予約確定確認モーダル */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">予約の確定</h3>
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6">予約を確定しますか？</p>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  戻る
                </button>
                <button
                  type="button"
                  onClick={executeConfirmReservation}
                  className="flex-1 py-2 px-4 rounded-lg text-white bg-cyan-500 hover:bg-cyan-600"
                >
                  確定する
                </button>
              </div>
            </div>
          </div>
        )}
      </ReservationLayout>
    </>
  )
}
